import { on } from "delegated-events";
import jump from "jump.js";
import { inView, stagger, timeline } from "motion";

const setupHeader = () => {
  const header = document.querySelector(".home-header");

  if (header.classList.contains("as-full")) {
    inView(header, () => {
      timeline([
        [
          header.querySelector(".media-block"),
          {
            opacity: 1,
          },
          { duration: 1.5 },
        ],
        [
          header.querySelector(".home-header-screen"),
          {
            opacity: 1,
            transform: "translate3d(0, 0, 0)",
          },
          { duration: 0.8, at: "<" },
        ],
        [
          header.querySelectorAll(".home-header-content"),
          {
            opacity: 1,
            transform: "translate3d(0, 0, 0)",
          },
          { duration: 0.8, delay: stagger(0.4) },
        ],
      ]);
    });
  }

  if (header.classList.contains("as-right")) {
    const isMobile = window.matchMedia("(max-width: 767px)").matches;

    const sequence = [];

    if (isMobile) {
      sequence.push([
        header.querySelector(".media-block"),
        {
          opacity: 1,
        },
        { duration: 1.5 },
      ]);
    } else {
      sequence.push([
        header.querySelector(".media-block"),
        {
          clipPath: "circle(50% at 50% 50%)",
          opacity: 1,
        },
        { duration: 1.5 },
      ]);

      sequence.push([
        header.querySelector(".media-block-circle"),
        {
          opacity: 1,
        },
        { duration: 1, at: "<" },
      ]);
    }

    sequence.push([
      header.querySelector(".home-header-screen"),
      {
        opacity: 1,
        transform: "translate3d(0, 0, 0)",
      },
      { duration: 0.8, at: "<" },
    ]);

    sequence.push([
      header.querySelectorAll(".home-header-content"),
      {
        opacity: 1,
        transform: "translate3d(0, 0, 0)",
      },
      { duration: 0.8, delay: stagger(0.4) },
    ]);

    inView(header, () => {
      timeline(sequence);
    });
  }

  const mediaCaption = header.querySelector(".header-media-caption");

  const handleMediaCaption = () => {
    const headerRect = header.getBoundingClientRect();
    const mediaBlock = header.querySelector(".header-media");
    const mediaBlockRect = mediaBlock.getBoundingClientRect();
    const captionHeight = mediaCaption.querySelector(".caption").offsetHeight;

    const heightDiff = headerRect.height - mediaBlockRect.height;

    if (heightDiff < 0) {
      const offset = heightDiff - captionHeight;

      mediaCaption.style.bottom = `${Math.abs(offset)}px`;
    } else {
      mediaCaption.style = null;
    }
  };

  if (mediaCaption) {
    handleMediaCaption();

    window.addEventListener("resize", handleMediaCaption);
  }
};

const scrollToPanel = (target) => {
  const siteHeaderHeight = document.querySelector(".site-header-inner").offsetHeight;
  let offset = parseInt(siteHeaderHeight) * -1;

  if (target.classList.contains("element-results")) {
    offset += 1;
  }

  return new Promise((resolve) => {
    jump(target, {
      duration: 600,
      offset,
      callback: () => {
        resolve();
      },
    });
  });
};

const setupPage = () => {
  const scrollContainer = document.querySelector(".panel-scroll-container");

  const isTouchDevice = "ontouchstart" in document.documentElement;
  const hasAutoScroll = scrollContainer.dataset.autoscroll !== undefined;
  const canAutoScroll = window.matchMedia("(min-height: 900px) and (min-width: 1024px)").matches;

  const allowAutoScroll = !isTouchDevice && hasAutoScroll && canAutoScroll;

  if (!allowAutoScroll) {
    return;
  }

  const siteHeader = document.querySelector(".site-header");
  const firstPanel = scrollContainer.firstElementChild;
  const lastPanel = scrollContainer.lastElementChild;

  let currentPanel = firstPanel;
  let scrollEnabled = true;
  let isScrolling = false;
  let scrollDirection = 1;
  let scrollThreshold = 5;

  const moveUp = async () => {
    if (currentPanel === firstPanel) {
      return;
    }

    isScrolling = true;

    const target = currentPanel?.previousElementSibling ?? lastPanel;

    if (target) {
      currentPanel = target;

      await scrollToPanel(target);
    }

    isScrolling = false;
  };

  const moveDown = async () => {
    isScrolling = true;

    const target = currentPanel.nextElementSibling;

    currentPanel = target;

    if (target) {
      await scrollToPanel(target);

      if (target === lastPanel) {
        disablePanelScroll();
      }
    }

    isScrolling = false;
  };

  on("element-results:complete", "html", (e) => {
    document.documentElement.classList.add("overflow-hidden");
    e.detail.classList.add("is-complete");
    moveDown();
    document.documentElement.classList.remove("overflow-hidden");
  });

  const handlePanelScroll = (e) => {
    scrollDirection = e.deltaY > 0 ? 1 : -1;

    if (isScrolling) {
      e.preventDefault();

      return false;
    }

    if (currentPanel?.nextElementSibling?.classList.contains("element-results") && scrollDirection < 0) {
      currentPanel?.nextElementSibling?.classList.remove("is-complete");
    }

    if (currentPanel?.classList.contains("element-results") && scrollDirection < 0) {
      setTimeout(() => {
        document.querySelector(".element-results").classList.remove("is-complete");
      }, 500);
    }

    if (currentPanel?.classList.contains("element-results") && scrollDirection > 0) {
      if (!currentPanel?.classList.contains("is-complete")) {
        return true;
      }
    }

    if (!scrollEnabled && scrollDirection < 0) {
      currentPanel = null;
      enablePanelScroll();
      moveUp();

      e.preventDefault();

      return false;
    }

    if (currentPanel === lastPanel && scrollDirection > 0) {
      disablePanelScroll();

      return true;
    }

    if (e.deltaY < -scrollThreshold) {
      moveUp();
    } else if (e.deltaY > scrollThreshold) {
      moveDown();
    }

    e.preventDefault();

    return false;
  };

  scrollContainer.addEventListener("wheel", handlePanelScroll, { passive: false });

  siteHeader.addEventListener(
    "wheel",
    (e) => {
      e.preventDefault();

      return false;
    },
    { passive: false },
  );

  const enablePanelScroll = () => {
    scrollEnabled = true;
  };

  const disablePanelScroll = () => {
    scrollEnabled = false;
  };

  enablePanelScroll();
};

const init = () => {
  if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    setupHeader();
    setupPage();
  }

  window.onbeforeunload = () => {
    document.documentElement.style.opacity = 0;
    window.scrollTo(0, 0);
  };
};

init();
